import "./styles.css";

import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";

import HelmetTags from "../components/helmet";
import Pricing from "../components/pricing";
import SignUpModal from "../landing/SignUpModal";
import { Testimonials } from "../landing/Testimonials";
import arrowRight from "../images/arrow-right-black.svg";
import arrowRightWhite from "../images/arrow-right.svg";
import { colors } from "../theme";
import mixpanel from "mixpanel-browser";
import { navigateTo } from "../utils/navigateTo";

mixpanel.init("cbf3854e17707951f0b0a0f67ac26da8", {
  debug: false,
  persistence: "localStorage",
});

const Logo = styled.img`
  height: 38px;
  margin-right: 10px;
`;

const HeaderContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 30px 15px 30px;
  width: 100%;

  ${({ scrollPosition }) =>
    scrollPosition > 50
      ? "background: white; border-bottom: 1px solid rgba(0, 0, 0, 0.1);"
      : "background: transparent"};

  transition: background-color 0.3s ease;
  @media (max-width: 1000px) {
    margin-top: 0px;
    padding: 15px;
  }
`;

const CTASection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 40px 0px 40px 0px;
  width: 100%;
`;

const CTAText = styled.div`
  font-size: 75px;
  font-weight: 500;
  line-height: 1;
  color: white;
  margin: 5px 0 20px 0;
  max-width: 750px;
  @media (max-width: 1000px) {
    font-size: 28px;
    text-align: center;
    max-width: 80%;
  }
`;

const HeaderButton = styled.button`
  background: black;
  color: white;
  border: 0px;
  width: fit-content;
  border-radius: 50px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 10px 14px 10px 14px;
  font-weight: 500;
  font-size: 16px;
  @media (max-width: 1000px) {
    padding: 12px;
    font-size: 16px;
  }
  transition: 150ms;
  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const CTATextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 20px;
  @media (max-width: 1000px) {
    align-items: center;
    max-width: 100%;
  }
`;

const FooterContainer = styled.div`
  background: ${colors.primary};
  background-image: linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)),
    url("https://res.cloudinary.com/frontly/image/upload/v1737959069/team-2_bzcgtm.jpg");
  background-size: cover;
  background-position: 10% 20%;
  width: calc(100% - 100px);
  border-radius: 40px;
  height: 400px;
  display: flex;
  justify-content: center;
  margin: 0 auto;
  @media screen and (max-width: 900px) {
    width: calc(100% - 60px);
  }
`;

const BlueContent = styled.div`
  position: relative;
  width: 100%;
  background: #f6f6f6;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Icon = styled.img`
  height: 25px;
`;

const Content = styled.div`
  margin: 0 auto;
  padding: 20px;
  width: 1350px;
  position: relative;
  z-index: 2;
  @media (max-width: 1000px) {
    width: 100%;
    padding: 16px;
  }
`;

const FeatureBlocksContent = styled(Content)`
  margin-top: 50px;
  @media (max-width: 1000px) {
    width: 100%;
    margin-top: 20px;
  }
`;

const Landing = () => {
  const [isOpen, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const isWindowDefined = typeof window !== "undefined";
  const [isMobile, setIsMobile] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  const [showSignupModal, setShowSignupModal] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 800);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Clean up event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isWindowDefined) {
      const getReferralObj = () => {
        const params = new URLSearchParams(window.location.search);
        const via = params.get("via");
        const ref = params.get("ref");

        return {
          via: via && via !== "null" ? via : null,
          ref: ref && ref !== "null" ? ref : null,
        };
      };

      const refferalObj = getReferralObj();
      mixpanel.track("Landing Page", refferalObj);
    }
  }, [isWindowDefined]);

  const header = (
    <HeaderContainer scrollPosition={scrollPosition}>
      <HeaderBar>
        <Logo src="https://res.cloudinary.com/frontly/image/upload/v1737557733/frontly_black_abjsh6_hn3daa.svg" />

        <HeaderLink
          onClick={() => {
            const element = document.getElementById("pricingSection");
            if (element) {
              // Scroll to active block
              element.scrollIntoView({ behavior: "smooth" });
            }
          }}
        >
          Pricing
        </HeaderLink>
        <HeaderLink onClick={() => window.open("https://help.frontly.ai")}>
          Support
        </HeaderLink>
        {/* <HeaderLink onClick={() => navigateTo("templates")}>
          Templates
        </HeaderLink> */}
      </HeaderBar>
      <HeaderBar>
        <HeaderLink
          onClick={() => window.open("https://discord.gg/8wnQndpBGU")}
        >
          Community
        </HeaderLink>
        <HeaderLink
          onClick={() => window.open("https://www.youtube.com/@frontlyapp")}
        >
          YouTube
        </HeaderLink>
        <HeaderLink primary onClick={() => navigateTo("login")}>
          Login
        </HeaderLink>
        <HeaderButton onClick={() => navigateTo("signup")}>
          Sign Up
        </HeaderButton>
      </HeaderBar>
      {showSignupModal && (
        <SignUpModal hide={() => setShowSignupModal(false)} prompt={prompt} />
      )}
    </HeaderContainer>
  );

  const logos = [
    {
      src: "https://res.cloudinary.com/curatorapp/image/upload/v1734630593/zapier-logo_zuwhnx.png",
      alt: "Zapier Logo",
      link: "https://intercom.help/curatorapp/en/articles/10303313-using-curator-s-zapier-integration",
    },
    {
      src: "https://res.cloudinary.com/curatorapp/image/upload/v1734630616/make-logo_mefsuc.png",
      alt: "Make Logo",
      link: "https://intercom.help/curatorapp/en/articles/10303118-using-curator-s-make-integration",
    },
    {
      src: "https://res.cloudinary.com/frontly/image/upload/v1659736210/1498px-Google_Sheets_logo__282014-2020_29.svg_edcf6y.png",
      alt: "n8n Logo",
      name: "Sheets",
    },
    {
      src: "https://res.cloudinary.com/frontly/image/upload/v1716142973/supabase-icon_akbjay.png",
      alt: "Supabase Logo",
      name: "Supabase",
    },
  ];

  const LogoRow = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
    @media (max-width: 1000px) {
      gap: 10px;
    }
  `;

  const LogoContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    padding: 10px;
    border-radius: 20px;
    height: 80px;
    width: 210px;
    @media (max-width: 800px) {
      height: 60px;
      width: 150px;
    }
  `;

  const WorksWith = styled.div`
    font-size: 20px;
    font-weight: 500;
    color: black;
  `;

  const WorksWithSection = styled.div`
    margin-top: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
  `;

  const LogoImage = styled.img`
    height: ${(p) => (p.small ? "30px" : "50px")};
    @media (max-width: 800px) {
      height: ${(p) => (p.small ? "20px" : "35px")};
    }
  `;

  const LogoName = styled.div`
    font-size: 26px;
    font-weight: 900;
    color: black;
    @media (max-width: 800px) {
      font-size: 20px;
    }
  `;

  const hero = (
    <NewHeroContent>
      <Title>Build with AI. Edit with no code.</Title>
      <Subtitle>
        Describe your app to start building custom software. No coding required.
      </Subtitle>

      <InputContainer>
        <Input
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="I want to build an app that..."
        />

        <ArrowButtonWrapper
          prompt={prompt && prompt.length > 0}
          onClick={() => {
            setShowSignupModal(true);
          }}
        >
          <Icon src={arrowRightWhite} style={{ cursor: "pointer" }} />
        </ArrowButtonWrapper>
      </InputContainer>
      <Examples>
        {examples.map((example, i) => (
          <Example key={i} onClick={() => setPrompt(example.prompt)}>
            {example.name}
          </Example>
        ))}
      </Examples>

      {/* <WorksWithSection>
        <WorksWith>Works with these platforms, and more:</WorksWith>
        <LogoRow>
          {logos.map((logo, index) => (
            <LogoContainer key={index}>
              <LogoImage
                key={index}
                src={logo.src}
                alt={logo.alt}
                small={logo.name}
                style={{
                  marginRight: logo.name === "Sheets" ? "5px" : "0px",
                }}
              />
              {logo.name && <LogoName>{logo.name}</LogoName>}
            </LogoContainer>
          ))}
        </LogoRow>
      </WorksWithSection> */}
    </NewHeroContent>
  );

  return (
    <Background>
      {header}
      <HelmetTags />
      <Content>{hero}</Content>

      <GreySection>
        <Testimonials />
        <Pricing isMobile={isMobile} />
        <AppFooter />
      </GreySection>
    </Background>
  );
};

export default Landing;

export const AppFooter = () => {
  return (
    <FooterContainer style={{ width: "100%", marginTop: "50px" }}>
      <Content>
        <CTASection>
          <CTATextContainer>
            <CTAText>Build your app for free today</CTAText>
            <TryFreeRow>
              <CTAButton onClick={() => navigateTo("signup")} large white>
                Get started for free
                <Icon
                  src={arrowRight}
                  style={{ margin: "0 0 0 5px", cursor: "pointer" }}
                />
              </CTAButton>
              <DemoButton
                onClick={() => window.open("https://demo-request.frontly.ai")}
              >
                Request a demo
                <Icon
                  src={arrowRightWhite}
                  style={{ margin: "0 0 0 5px", cursor: "pointer" }}
                />
              </DemoButton>
            </TryFreeRow>
          </CTATextContainer>
        </CTASection>
      </Content>
    </FooterContainer>
  );
};

const examples = [
  {
    name: "Client Login Portal",
    prompt:
      "Create a client login portal where each user sees personalized dashboards, secure document storage, and a messaging system, accessible 24/7 with unique permissions for clients and administrators.",
  },
  {
    name: "Events Management Platform",
    prompt:
      "Build an events management platform with functionalities including attendee registration, automated email reminders, real-time RSVP tracking, event calendar integration, and customizable event pages for seamless management.",
  },
  {
    name: "Personal Daily Habit Tracker",
    prompt:
      "Design a visually appealing personal daily habit tracker app featuring habit logging, progress visualizations, daily reminders, and motivational notifications, with an intuitive dashboard to review long-term trends.",
  },
  {
    name: "Expense Tracker",
    prompt:
      "Generate an expense tracking app where users can log expenses, categorize transactions automatically, visualize monthly budgeting insights through interactive charts, and integrate securely with their bank accounts for real-time updates.",
  },
  {
    name: "Real Estate Listings Website",
    prompt:
      "Set up a real estate listings website featuring automated property feeds, advanced filters for buyers (price, location, property type), email notifications for new matching listings, and integrated lead management tools for agents.",
  },
];

// Define the keyframes for the gradient animation
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const Background = styled.div`
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 18px;
  max-width: 600px;
  width: 100%;
  position: relative;
  margin: 20px 0;
  box-shadow: var(--card-shadow);
  border-radius: 15px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  background: rgba(0, 0, 0, 0.03);
`;

// Create a styled component for animated text
const Title = styled.h1`
  font-size: 53px;
  font-weight: 700;
  padding: 0px;
  margin: 0 0 10px 0;
  background: linear-gradient(45deg, blue, indigo, violet, red, orange);
  background-size: 400% 400%;
  animation: ${gradientAnimation} 30s ease infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  text-align: center;
  @media (max-width: 1000px) {
    font-size: 50px;
  }
`;

const Input = styled.textarea`
  border-radius: 15px;
  border: 0px;
  resize: none;
  font-size: 18px;
  min-height: 80px;
  margin-right: 55px;
  outline: none;
  background: transparent;
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const NewHeroContent = styled.div`
  margin: 0 auto;
  width: calc(100% - 40px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-top: 50px;
  @media (max-width: 1000px) {
    margin-top: 30px;
  }
`;

const Subtitle = styled.div`
  font-size: 20px;
  font-weight: 400;
  color: var(--text);
  text-align: center;
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const Examples = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  max-width: 750px;
  align-items: center;
  justify-content: center;
`;

const Example = styled.div`
  padding: 12px 16px 12px 16px;
  border-radius: 30px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  &:hover {
    background: rgba(0, 0, 0, 0.02);
    color: black;
  }
  font-size: 16px;
  @media (max-width: 1000px) {
    font-size: 14px;
    padding: 10px 10px 10px 10px;
  }
`;

const ArrowButtonWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: black;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 18px;
  cursor: pointer;

  background: linear-gradient(45deg, blue, indigo, violet, red, orange);
  background-size: 1000% 600%;
  animation: ${gradientAnimation} 30s ease infinite;
`;

const CTAButton = styled.div`
  background: white;
  color: black;
  width: fit-content;
  border-radius: 50px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 20px;
  font-weight: 500;
  font-size: 20px;
  height: 70px;
  @media (max-width: 1000px) {
    padding: 12px;
    font-size: 15px;
    height: 50px;
  }
  transition: 250ms;
  &:hover {
    filter: brightness(90%);
  }
`;

const DemoButton = styled.div`
  color: white;
  padding: 20px;
  font-weight: 500;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
  @media (max-width: 1000px) {
    padding: 12px;
    font-size: 16px;
  }
`;

const TryFreeRow = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;
  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const GreySection = styled.div`
  background: ${colors.lightGrey};
  background: #f6f6f6;
  width: 100%;
  padding: 70px 50px 100px 50px;
  margin-top: 100px;
  border-radius: 50px 50px 0 0;
  @media (max-width: 800px) {
    padding: 20px;
    margin-top: 0px;
  }
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderLink = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #222222;
  cursor: pointer;
  padding: 10px;
  @media (max-width: 1000px) {
    display: none;
  }
  &:hover {
    border-radius: 30px;
    background: rgba(0, 0, 0, 0.05);
  }
`;
